import styled, { css } from "styled-components";
import penSolid from "../../../assets/pen-solid.svg";
import chevronDownSolid from "../../../assets/chevron-down-solid.svg";
import { theme } from "../../../styles/theme";

const menuBackgroundColor = "white";
const menuItemSpanColor = "black";

// Filters
const penFilter =
  "brightness(0) saturate(100%) invert(59%) sepia(83%) saturate(427%) hue-rotate(341deg) brightness(98%) contrast(95%)";

// Spec 20261028-130000 AC-9: the rail, the content beside it and the labels move together, in the same tokens; with
// reduced motion they change at once.
const railMotion = `${theme.motion.durationBase} ${theme.motion.easeOut}`;
const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const headerHeight = "3rem";
const footerHeight = "2.5rem";

export const Container = styled.div`
  overflow: hidden;
`;

// Mobile toggle button (outside the aside)
export const MobileToggleButton = styled.div<{ $isOpen: boolean }>`
  display: none;
  
  @media screen and (max-width: 768px) {
    display: flex;
    position: fixed;
    top: 1.5rem;
    left: 1rem;
    z-index: 10000;
    cursor: pointer;
    background-color: white;
    border: 1px solid #e9e9e9;
    border-radius: 8px;
    width: 45px;
    height: 45px;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transition: left ${railMotion}, transform ${railMotion};
    ${noMotion}

    ${({ $isOpen }) =>
      $isOpen &&
      css`
        left: 255px;
      `}

    &:active {
      transform: scale(0.95);
    }

    & svg {
      width: 25px;
      height: 25px;
      fill: #374151;
    }
  }
`;

export const HeaderContainer = styled.header`
  box-sizing: border-box;
  display: flex;
  height: 72px;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  position: sticky;
  background-color: ${theme.colors.surface};
  border-bottom: 1px solid ${theme.colors.border};
  padding: 0 2.5rem;
  top: 0;
  transition: box-shadow 0.3s;
  width: 100%;
  z-index: 100;

  @media screen and (max-width: 768px) {
    padding-left: 4rem;
    padding-right: 1rem;
  }
`;

export const HeaderLogo = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  height: 3rem;
  align-items: center;
  padding: 0 0.5rem;
  margin-bottom: 3rem;
  justify-content: space-between;
  overflow: hidden;

  @media screen and (max-width: 768px) {
    width: calc(100% - 1rem);
  }

  & > .icon {
    transition: opacity ${railMotion}, width ${railMotion};
    ${noMotion}
    ${({ $isOpen }) =>
      !$isOpen &&
      css`
        opacity: 0;
        width: 0;
        overflow: hidden;
      `}
    @media screen and (max-width: 768px) {
      width: 100%;
    }
    & > svg {
      height: calc(${headerHeight} - 0.6rem);
      width: fit-content;
    }
  }

  & > .mark {
    display: none;
    width: 36px;
    height: 36px;
    object-fit: contain;
  }

  ${({ $isOpen }) =>
    !$isOpen &&
    css`
      flex-direction: column;
      height: auto;
      gap: 10px;
      padding: 0;
      justify-content: center;
      & > .mark {
        display: block;
      }
    `}
`;

/**
 * The collapse button, as the ERP draws it (owner smoke, 2026-11-04: "o abrir e fechar, logo e tal, eu quero igual do
 * ERP"): chevrons in a 36 px square that takes the brand tint on hover, beside the logo when open and under the
 * symbol when collapsed.
 */
export const AsideToggle = styled.button<{ $isOpen: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: ${theme.radii.sm};
  background: ${({ $isOpen }) => ($isOpen ? "transparent" : theme.colors.tintOrange50)};
  color: ${({ $isOpen }) => ($isOpen ? theme.colors.fg2 : theme.colors.primary)};
  cursor: pointer;
  transition: background ${railMotion}, color ${railMotion};
  ${noMotion}

  &:hover {
    background: ${theme.colors.tintOrange50};
    color: ${theme.colors.primary};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

export const HeaderLeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-right: auto;
  min-width: 0;
  flex-wrap: wrap;
`;

export const HeaderHospitalActive = styled.div<{ $noEdit?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${theme.colors.surface};
  border: 1.5px solid ${theme.colors.borderStrong};
  border-radius: ${theme.radii.sm};
  padding: 0.55rem 0.9rem;
  min-height: 44px;
  box-sizing: border-box;
  pointer-events: none;

  ${({ $noEdit }) =>
    !$noEdit &&
    css`
      pointer-events: all;
      & > span {
        cursor: pointer;
        position: relative;
        transition: color 0.2s;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        &:hover {
          color: ${theme.colors.senneOrange};
        }
        & .icon {
          display: flex;
          align-items: center;
        }
      }
    `}

  & > span {
    font-weight: 600;
    font-size: 14px;
    color: ${theme.colors.fg1};
    white-space: nowrap;
  }
`;

export const HeaderRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

export const UserMenuAnchor = styled.div`
  position: relative;
`;

export const UserMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 240px;
  z-index: ${theme.zIndex.modal};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.xl};
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const UserMenuHeader = styled.div`
  padding: 8px 12px 12px;
  border-bottom: 1px solid ${theme.colors.gray100};
  margin-bottom: 6px;

  .name {
    font-weight: 700;
    font-size: 15px;
    color: ${theme.colors.fg1};
  }
  .role {
    font-size: 13px;
    color: ${theme.colors.fg3};
  }
`;

export const UserMenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px;
  padding: 0 12px;
  border: none;
  background: transparent;
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.fg1};
  font-size: 15px;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: ${theme.colors.gray50};
  }

  svg {
    width: 18px;
    height: 18px;
    flex: none;
  }
`;

export const HeaderUser = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  border: none;
  background: transparent;
  padding: 0;
  font-family: inherit;
  cursor: pointer;

  & .header_user_content {
    line-height: 1.15;

    @media screen and (max-width: 500px) {
      display: none;
    }
  }

  & .header_user_content_name {
    font-weight: 600;
    font-size: 14px;
    color: ${theme.colors.fg1};
    text-align: start;
  }

  & .header_user_role {
    font-weight: 400;
    font-size: 12px;
    color: ${theme.colors.fg3};
    text-align: start;
  }

  & .header_user_image {
    flex: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: ${theme.colors.tintOrange100};
    color: ${theme.colors.senneOrange};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
  }
`;

// On a phone 100vh runs under the browser bars and hid Sair, which is why the list was held at 50vh (0afaf977);
// 100dvh is the visible height, so the list fills what is left and scrolls only when it must (fallback first).
export const AsideContainer = styled.aside<{ $isOpen: boolean }>`
  background-color: ${theme.colors.surface};
  box-sizing: border-box;
  height: 100vh;
  border-right: 1px solid ${theme.colors.border};
  position: fixed;
  transition: width ${railMotion}, transform ${railMotion};
  ${noMotion}
  width: 76px;
  z-index: 10001;
  padding: 1.75rem 0.75rem;
  border-radius: 0;
  display: flex;
  flex-direction: column;

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          width: 250px;
          & > ul > li > a {
            justify-content: center;
            align-items: center;
            padding: 0.2rem 1rem !important;
          }
          & ul > li > a > span {
            display: block;
            white-space: nowrap;
            max-width: 100%;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          & > ul > li > a > .external-mark {
            flex-shrink: 0;
          }
          & > ul > li.--is_dropdown > ul {
            background-color: ${menuBackgroundColor};
            padding-left: 10px;
            color: ${menuItemSpanColor};
            position: relative !important;
            left: auto !important;
            top: auto !important;
            width: 100% !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            & > li > a {
              justify-content: flex-start !important;
              padding-left: 2rem !important;
            }
          }
        `
      : css`
          & > ul > li > a {
            justify-content: center !important;
            padding: 0.2rem 0 !important;
          }
          & > ul > li.--is_dropdown {
            position: relative;
            & > a > span::after {
              display: none !important;
            }
            & > ul {
              position: absolute !important;
              left: 60px !important;
              top: 0 !important;
              width: 200px !important;
              height: auto !important;
              max-height: 80vh !important;
              overflow-y: auto !important;
              display: none !important;
              box-shadow: 1px 1px 4px 1px rgba(0, 0, 0, 0.2) !important;
              background-color: ${menuBackgroundColor} !important;
              border-radius: 0 0.5rem 0.5rem 0 !important;
              z-index: 102 !important;
              transition: none !important;
              & > li > a {
                justify-content: flex-start !important;
                padding-left: 1rem !important;
              }
            }
            &:hover > ul {
              display: block !important;
            }
          }
          @media screen and (min-width: 768px) {
            & > ul > li > a {
              gap: 0;
            }
            & > ul > li > a > span {
              opacity: 0;
              max-width: 0;
              overflow: hidden;
            }
            & > ul > li > a > .external-mark {
              position: absolute;
              top: ${theme.spacing[2]};
              right: ${theme.spacing[1]};
            }
          }
        `}

  @media screen and (max-width: 768px) {
    width: 250px;
    height: 100vh;
    height: 100dvh;
    transform: translateX(-100%);
    
    ${({ $isOpen }) =>
      $isOpen &&
      css`
        transform: translateX(0);
      `}
  }

  & > ul {
    display: flex;
    flex-direction: column;
    list-style: none;
    overflow-y: auto;
    min-height: 0;

    &:first-of-type {
      flex: 1;
    }

    @media screen and (max-width: 768px) {
      overflow: auto;
    }

    & > li {
      align-content: center;
      background-color: ${menuBackgroundColor};
      color: ${menuItemSpanColor};
      transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-left 0.2s ease-in-out;

      & input {
        background-color: transparent;
        width: 100%;
        outline: none;
        padding: 0.5rem 0.8rem;
        color: ${menuItemSpanColor} !important;
        &::placeholder {
          color: ${menuItemSpanColor} !important;
          opacity: 0.4;
        }
      }

      & > ul {
        height: 0px;
        max-height: fit-content;
        overflow: hidden;
        transition: height 0.8s;
      }

      &:hover > ul,
      & > ul:has(input:focus, .--active) {
        @media screen and (min-width: 768px) {
          ${({ $isOpen }: any) =>
            $isOpen &&
            css`
              height: auto;
              max-height: 80vh;
              overflow-y: auto;
            `}
        }
        @media screen and (max-width: 768px) {
          height: auto;
          max-height: 80vh;
          overflow-y: auto;
        }
      }

      &.--change_hospital {
        @media screen and (min-width: 800px) {
          display: none;
        }
      }

      &.--custom_placeholder {
        display: block;
      }

      &.--logout_bottom {
        font-weight: bold;
      }

      &.--group_start {
        border-top: 1px solid ${theme.colors.border};
        margin-top: ${theme.spacing[2]};
        padding-top: ${theme.spacing[2]};
      }

      &.--hidden {
        display: none;
      }

      /* Hover (item não-ativo): tint mais leve, sem negrito. */
      &:hover:not(:has(ul:hover)):not(.--active) > a,
      &:has(> a:hover):not(.--active) > a,
      &.--is_dropdown:hover:not(:has(ul:hover)) > a {
        background-color: ${theme.colors.secondaryBg};
        color: ${theme.colors.senneOrangeLight};
        & > span {
          color: var(--link);
        }
      }

      /* Ativo: peach preenchido + texto e ícone na MESMA cor da marca (senne-orange-light). */
      &.--active > a,
      &:has(.--active) > a {
        background-color: ${theme.colors.tintOrange100};
        color: ${theme.colors.senneOrangeLight};
        font-weight: 700;
        & > span {
          color: var(--link);
        }
      }

      &.--is_dropdown > a > span::after {
        content: "";
        display: block;
        background: url(${chevronDownSolid}) no-repeat center center / 10px 10px;
        width: 10px;
        height: 10px;
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
        transition: top 0.3s;
      }

      & > a {
        align-items: center;
        cursor: pointer;
        display: flex;
        gap: 0.75rem;
        position: relative;
        min-height: 48px;
        color: ${theme.colors.fg2};
        border-radius: ${theme.radii.sm};
        font-size: ${theme.fontSize.md};
        padding: 0.2rem 0.75rem;

        & > span {
          transition: color 0.3s, opacity ${railMotion};
          ${noMotion}
          white-space: nowrap;
          width: 100%;
          position: relative;
        }

        & > i {
          align-content: center;
          display: flex;
          transition: none;
          & > svg {
            flex-shrink: 0;
            height: 20px;
            width: 20px;
          }
        }
      }
    }
  }
`;

/**
 * Landmark de conteúdo. Sem ele a área logada não tinha `<main>`: leitor de tela e navegação por
 * landmark não tinham para onde ir depois dos ~11 itens do menu.
 */
export const MainArea = styled.main`
  min-height: 0;
`;

/** Primeiro foco da página, visível só quando focado — pula os itens do menu. */
export const SkipLink = styled.a`
  position: absolute;
  left: -9999px;
  top: 0;
  z-index: 10002;

  &:focus {
    left: 8px;
    top: 8px;
    padding: 0.625rem 1rem;
    background: ${theme.colors.surface};
    color: ${theme.colors.fg1};
    border: 1px solid ${theme.colors.border};
    border-radius: ${theme.radii.md};
    box-shadow: ${theme.shadows.md};
    font-size: ${theme.typography.fs.sm};
    font-weight: ${theme.typography.fw.semibold};
    text-decoration: none;
  }
`;

export const Content = styled.div<{ $isOpen: boolean }>`
  height: 100vh;
  overflow: auto;
  margin-left: 76px;
  background-color: ${theme.colors.senneCream};
  transition: margin-left ${railMotion};
  ${noMotion}

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      margin-left: 250px;
    `}

  @media screen and (max-width: 768px) {
    margin-left: 0px;
  }
`;

export const FooterContainer = styled.footer`
  align-items: center;
  background-color: ${menuBackgroundColor};
  bottom: 0;
  box-shadow: 0 -1px 4px 1px rgba(0, 0, 0, 0.2);
  display: flex;
  height: ${footerHeight};
  justify-content: right;
  position: sticky;
  width: 100%;
  z-index: 101;

  @media screen and (max-width: 800px) {
    justify-content: center;
  }

  @media screen and (max-width: 500px) {
    font-size: 13px;
  }
`;

export const FooterHospitalActive = styled.div<{ $noEdit?: boolean }>`
  align-content: center;
  display: none;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 0.2rem;
  width: 100%;
  text-align: center;

  ${({ $noEdit }) =>
    !$noEdit &&
    css`
      pointer-events: all;
      & > span {
        cursor: pointer;
        position: relative;
        transition: text-shadow 0.3s;
        &:hover {
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.6);
        }
        &::after {
          background: url(${penSolid}) no-repeat center center / cover;
          content: "";
          display: block;
          filter: ${penFilter};
          height: 12px;
          position: absolute;
          right: -1rem;
          top: calc(50% - 0.2rem);
          transform: translateY(-50%);
          width: 12px;
        }
      }
    `}

  @media screen and (max-width: 800px) {
    display: block;
  }

  & > span {
    font-weight: 600;
    font-size: 14.5px;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.4);
    text-transform: uppercase;
  }
`;

export const Datetime = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 12px 0;
  margin-top: 8px;
  border-top: 1px solid ${theme.colors.border};

  span {
    font-size: ${theme.fontSize.sm};
    color: ${theme.colors.fg3};
    text-transform: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

/* Banner de personificação — visível enquanto operação está "vendo como" outro usuário. */
export const ImpersonationBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 10px 2.5rem;
  background: ${theme.colors.warningSoft};
  border-bottom: 1px solid ${theme.colors.warning};
  color: ${theme.colors.fg1};
  font-size: 14px;

  b {
    font-weight: 700;
  }

  @media screen and (max-width: 768px) {
    padding-left: 4rem;
  }
`;

export const BannerBtn = styled.button`
  flex: none;
  height: 36px;
  padding: 0 16px;
  border: 1.5px solid ${theme.colors.warning};
  border-radius: ${theme.radii.sm};
  background: transparent;
  color: ${theme.colors.fg1};
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${theme.colors.warning};
    color: #fff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;